import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { Calculator, CheckCircle2, Clock, XCircle } from 'lucide-react';
import { useMemo } from 'react';

import type { DevelopmentDetailDataModel, DevelopmentDetailAiAnalysisResponse, PbiDecompositionModel } from '@/domain/models/development.js';
import type { AiChatRepository } from '@/domain/repositories/ai-chat-repository.js';
import { AiScoreGauge } from '@/presentation/components/index.js';
import { useApp } from '@/presentation/hooks/use-app.js';
import { EfChatPanel } from '@/presentation/pages/developments/chat/ef-chat-panel.js';
import { PBI_DECOMPOSITION_ENABLED } from '@/shared/constants/feature-flags.js';
import { formatDate } from '@/shared/utils/format-date.js';
import { formatFileSize } from '@/shared/utils/format-file-size.js';

type Props = {
    data: DevelopmentDetailDataModel;
    pbiDecomposition: PbiDecompositionModel | null;
    isCf: boolean;
    analysisJustCompleted: boolean;
    onClearAnalysisJustCompleted: () => void;
    efUploadSuccessVersion: number | null;
    onClearEfUploadSuccess: () => void;
    efUploadError: string | null;
    onClearEfUploadError: () => void;
    onUploadEf: (file: File) => void;
    onDeleteEf: () => Promise<boolean>;
    onDownloadEf: (version?: number) => Promise<void>;
    onSendToDevelopment: () => Promise<boolean>;
    onRetryPbiDecomposition: () => Promise<boolean>;
    aiChatRepository: AiChatRepository;
    onAnalysisCommitted?: () => void;
};

function getSeverityColor(severity: string): 'error' | 'warning' | 'default' {
    switch (severity.toUpperCase()) {
        case 'ALTA':
            return 'error';
        case 'MEDIA':
        case 'MÉDIA':
            return 'warning';
        default:
            return 'default';
    }
}

function sortAnalyses(analyses: DevelopmentDetailAiAnalysisResponse[]): DevelopmentDetailAiAnalysisResponse[] {
    return [...analyses].sort((a, b) => b.attemptNumber - a.attemptNumber);
}

function AnalysisResultCard({ analysis }: { analysis: DevelopmentDetailAiAnalysisResponse }) {
    const { translate, useAuthStore } = useApp();
    const user = useAuthStore((s) => s.user);
    const canViewSuggestedEstimates = Boolean(user?.isGlobalAdmin) || user?.profile === 'DEV' || user?.profile === 'LT' || user?.profile === 'GPA';
    const statusColor = analysis.passed ? 'success' : 'error';
    const sapMode = analysis.sapValidationMode ?? 'degraded';
    const sapReasonKey = analysis.sapValidationError ? `efIa.sapErrors.${analysis.sapValidationError}` : 'efIa.sapDegradedUnknown';
    const sapLabel =
        sapMode === 'live'
            ? translate('efIa.sapLiveBadge', { system: analysis.sapValidationSystem ?? '—' })
            : translate('efIa.sapDegradedBadge', { reason: translate(sapReasonKey) });

    return (
        <Stack spacing={2}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 1,
                        p: 2,
                        borderRadius: 2,
                        bgcolor: (theme) => alpha(theme.palette.text.primary, 0.03),
                        flexShrink: 0
                    }}
                >
                    <AiScoreGauge score={analysis.score} passed={analysis.passed} />
                    <Chip
                        size="small"
                        icon={analysis.passed ? <CheckCircle2 size={14} /> : <XCircle size={14} />}
                        label={analysis.passed ? translate('efIa.approvedLabel') : translate('efIa.rejectedLabel')}
                        sx={{
                            bgcolor: (theme) => alpha(theme.palette[statusColor].main, 0.12),
                            color: (theme) => theme.palette[statusColor].dark,
                            fontWeight: 600,
                            '& .MuiChip-icon': { color: (theme) => theme.palette[statusColor].dark }
                        }}
                    />
                    <Chip
                        size="small"
                        label={sapLabel}
                        color={sapMode === 'live' ? 'success' : 'default'}
                        variant={sapMode === 'live' ? 'filled' : 'outlined'}
                        sx={{ fontWeight: 600 }}
                    />
                    <Box
                        sx={{
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 1,
                            bgcolor: (theme) => alpha(theme.palette.info.main, 0.12),
                            color: (theme) => theme.palette.info.dark,
                            fontSize: 12,
                            fontWeight: 600
                        }}
                    >
                        {translate('efIa.minScoreLabel', { n: analysis.minScoreUsed })}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                        {translate('efIa.attemptNumber', { n: analysis.attemptNumber })}
                    </Typography>
                    <Typography variant="caption" color="text.disabled">
                        {formatDate(analysis.analyzedAt).replace(' ', ', ')}
                    </Typography>
                </Box>
                <Stack spacing={1} justifyContent="center">
                    {canViewSuggestedEstimates && analysis.suggestedComplexity && (
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Calculator size={16} style={{ opacity: 0.6, flexShrink: 0 }} />
                            <Typography variant="body2">
                                {translate('efIa.suggestedComplexityLabel')} <b>{translate(`complexityLabels.${analysis.suggestedComplexity}`)}</b>
                            </Typography>
                        </Stack>
                    )}
                    {canViewSuggestedEstimates && analysis.suggestedHours !== null && (
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Clock size={16} style={{ opacity: 0.6, flexShrink: 0 }} />
                            <Typography variant="body2">
                                {translate('efIa.suggestedHoursLabel')} <b>{analysis.suggestedHours}h</b>
                            </Typography>
                        </Stack>
                    )}
                    {analysis.strictnessLevelUsed !== null && (
                        <Typography variant="caption" color="text.secondary">
                            {translate('efIa.strictnessLevelUsed', { n: analysis.strictnessLevelUsed })}
                        </Typography>
                    )}
                    {analysis.modelUsed && (
                        <Typography variant="caption" color="text.secondary">
                            {translate('efIa.modelUsed', { model: analysis.modelUsed })}
                        </Typography>
                    )}
                    {analysis.providerUsed && (
                        <Typography variant="caption" color="text.secondary">
                            {translate('efIa.providerUsed', { provider: analysis.providerUsed })}
                        </Typography>
                    )}
                </Stack>
            </Stack>
            {analysis.analysisSummary && <Typography variant="body2">{analysis.analysisSummary}</Typography>}
            {analysis.blockingQuestions && analysis.blockingQuestions.length > 0 && (
                <Box>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 1, mb: 0.5 }}>
                        {translate('efIa.blockingQuestions', { n: analysis.blockingQuestions.length })}
                    </Typography>
                    <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 2 }}>
                        {analysis.blockingQuestions.map((question, index) => (
                            <Typography key={index} component="li" variant="body2">
                                {question}
                            </Typography>
                        ))}
                    </Stack>
                </Box>
            )}
            {analysis.assumptions && analysis.assumptions.length > 0 && (
                <Box>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 1, mb: 0.5 }}>
                        {translate('efIa.assumptions', { n: analysis.assumptions.length })}
                    </Typography>
                    <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 2 }}>
                        {analysis.assumptions.map((assumption, index) => (
                            <Typography key={index} component="li" variant="body2">
                                {assumption}
                            </Typography>
                        ))}
                    </Stack>
                </Box>
            )}
            {analysis.criteriaScores && analysis.criteriaScores.length > 0 && (
                <Box>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 1, mb: 0.5 }}>
                        {translate('efIa.criteriaScoresTitle')}
                    </Typography>
                    <Stack spacing={0.5}>
                        {analysis.criteriaScores.map((criteria) => (
                            <Box key={criteria.code} sx={{ p: 1, borderRadius: 1, border: 1, borderColor: 'divider' }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography variant="body2" fontWeight={500}>
                                        {criteria.name} ({criteria.code})
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {criteria.score}/{criteria.maxScore}
                                    </Typography>
                                </Stack>
                            </Box>
                        ))}
                    </Stack>
                </Box>
            )}
            {analysis.suggestedFixes && analysis.suggestedFixes.length > 0 && (
                <Box>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 1, mb: 0.5 }}>
                        {translate('efIa.adjustmentSuggestions', { n: analysis.suggestedFixes.length })}
                    </Typography>
                    <Stack spacing={1}>
                        {analysis.suggestedFixes.map((fix, index) => (
                            <Box key={index} sx={{ p: 1, borderRadius: 1, border: 1, borderColor: 'divider' }}>
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                                    <Chip label={fix.severity} color={getSeverityColor(fix.severity)} size="small" />
                                    {fix.pageRef && (
                                        <Typography variant="caption" color="text.secondary">
                                            {translate('efIa.pageRefLabel', { page: fix.pageRef })}
                                        </Typography>
                                    )}
                                </Stack>
                                <Typography variant="body2">{fix.description}</Typography>
                                {fix.reference && (
                                    <Typography variant="caption" color="text.secondary">
                                        {fix.reference}
                                    </Typography>
                                )}
                            </Box>
                        ))}
                    </Stack>
                </Box>
            )}
        </Stack>
    );
}

export function EfIaTab({
    data,
    pbiDecomposition,
    isCf,
    analysisJustCompleted,
    onClearAnalysisJustCompleted,
    efUploadSuccessVersion,
    onClearEfUploadSuccess,
    efUploadError,
    onClearEfUploadError,
    onUploadEf,
    onDeleteEf,
    onDownloadEf,
    onSendToDevelopment,
    onRetryPbiDecomposition,
    aiChatRepository,
    onAnalysisCommitted
}: Props) {
    const { translate } = useApp();
    const sortedAnalyses = useMemo(() => sortAnalyses(data.aiAnalyses), [data.aiAnalyses]);
    const selectedAnalysis = sortedAnalyses[0] ?? null;
    const hasPassedAnalysis = data.aiAnalyses.some((a) => a.passed);
    const efMetaLabel = (doc: { version: number; fileSizeBytes: string | null }): string =>
        `${translate('efIa.versionEntryLabel', { v: doc.version })}${doc.fileSizeBytes ? ` · ${formatFileSize(Number(doc.fileSizeBytes))}` : ''}`;

    const latestAnalyzedAt = sortedAnalyses[0] ? new Date(sortedAnalyses[0].analyzedAt).getTime() : 0;
    const efUploadedAt = data.currentEfDocument ? new Date(data.currentEfDocument.uploadedAt).getTime() : 0;
    const efPendingEvaluation =
        Boolean(data.currentEfUrl) &&
        (data.status === 'EF_EM_ELABORACAO' || data.status === 'EF_REJEITADA_IA') &&
        (sortedAnalyses.length === 0 || efUploadedAt >= latestAnalyzedAt);
    const chatUnlocked = sortedAnalyses.length > 0 && (data.status === 'EF_VALIDADA_IA' || data.status === 'EF_REJEITADA_IA') && !efPendingEvaluation;
    const canApprove = isCf && data.status === 'EF_VALIDADA_IA' && hasPassedAnalysis;
    const canDeleteEf =
        isCf &&
        Boolean(data.currentEfDocument) &&
        (data.status === 'EF_EM_ELABORACAO' || data.status === 'EF_EM_VALIDACAO_IA' || data.status === 'EF_REJEITADA_IA' || data.status === 'EF_VALIDADA_IA');

    const pbiCard =
        PBI_DECOMPOSITION_ENABLED && pbiDecomposition ? (
            <Card variant="outlined">
                <CardContent>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                        {translate('efIa.pbiBacklogTitle')}
                    </Typography>
                    {pbiDecomposition.isProcessing() && (
                        <Stack direction="row" spacing={1} alignItems="center">
                            <CircularProgress size={16} />
                            <Typography variant="body2" color="text.secondary">
                                {translate('efIa.pbiGenerating')}
                            </Typography>
                        </Stack>
                    )}
                    {pbiDecomposition.hasError() && (
                        <Stack spacing={1}>
                            <Typography variant="body2" color="error">
                                {translate('efIa.pbiGenerationFailed')}
                                {pbiDecomposition.errorMessage ? `: ${pbiDecomposition.errorMessage}` : ''}
                            </Typography>
                            <Button size="small" variant="outlined" onClick={() => onRetryPbiDecomposition()}>
                                {translate('efIa.retryPbi')}
                            </Button>
                        </Stack>
                    )}
                    {pbiDecomposition.isCompleted() && (
                        <Stack spacing={1}>
                            {(pbiDecomposition.pbis ?? []).map((pbi, index) => (
                                <Box key={pbi.number ?? index} sx={{ p: 1.5, borderRadius: 1, border: 1, borderColor: 'divider' }}>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <Chip label={pbi.estimate ?? '—'} size="small" variant="outlined" />
                                        <Typography variant="body2" fontWeight={500}>
                                            {pbi.title ?? '—'}
                                        </Typography>
                                    </Stack>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                        {pbi.description ?? '—'}
                                    </Typography>
                                </Box>
                            ))}
                        </Stack>
                    )}
                </CardContent>
            </Card>
        ) : null;

    return (
        <Stack spacing={2} sx={{ flex: 1, minHeight: 0 }}>
            {analysisJustCompleted && selectedAnalysis && (
                <Alert severity={selectedAnalysis.passed ? 'success' : 'warning'} onClose={onClearAnalysisJustCompleted}>
                    {selectedAnalysis.passed ? translate('efIa.analysisCompletedSuccess') : translate('efIa.analysisCompletedRejected')}
                </Alert>
            )}
            {data.status === 'EF_REJEITADA_IA' && selectedAnalysis && (
                <Alert severity="warning">{translate('efIa.efRejectedMsg', { score: selectedAnalysis.score, min: selectedAnalysis.minScoreUsed })}</Alert>
            )}
            {data.status === 'EF_VALIDADA_IA' && <Alert severity="success">{translate('efIa.efApprovedMsg')}</Alert>}
            {efUploadError !== null && (
                <Alert severity="error" onClose={onClearEfUploadError}>
                    {efUploadError}
                </Alert>
            )}
            {efUploadSuccessVersion !== null && (
                <Alert severity="success" onClose={onClearEfUploadSuccess}>
                    {translate('efIa.uploadSuccess', { version: efUploadSuccessVersion })}
                </Alert>
            )}
            <EfChatPanel
                developmentId={data.id}
                aiChatRepository={aiChatRepository}
                analyses={sortedAnalyses}
                renderAnalysis={(analysis) => <AnalysisResultCard analysis={analysis} />}
                onVerdict={onAnalysisCommitted}
                isAnalysisInProgress={data.status === 'EF_EM_VALIDACAO_IA'}
                efPendingEvaluation={efPendingEvaluation}
                chatUnlocked={chatUnlocked}
                canApprove={canApprove}
                isCf={isCf}
                sapConnectionConfigured={data.sapConnectionConfigured}
                efAttachments={data.efVersions.map((doc) => ({
                    id: doc.id,
                    fileName: doc.fileName,
                    metaLabel: efMetaLabel(doc),
                    onDownload: () => {
                        void onDownloadEf(doc.version);
                    },
                    canDelete: doc.isCurrent && canDeleteEf
                }))}
                onUploadEf={onUploadEf}
                onApproveEf={() => {
                    void onSendToDevelopment();
                }}
                onDeleteEf={() => {
                    void onDeleteEf();
                }}
                footerSlot={pbiCard}
            />
        </Stack>
    );
}
